import { unwrapResult } from "@reduxjs/toolkit";
import { getApiErrorMessage } from "utils/errors";

// A thunk skipped by its `condition` (e.g. a feature disabled in sandbox) or
// aborted is not a failure: there is nothing to unwrap and nothing to report.
const isSkippedThunk = (actionResult) =>
  actionResult?.meta?.condition === true || actionResult?.meta?.aborted === true;

export const createCustomDispatch = (dispatch) => {
  return async (actionCreator) => {
    try {
      const actionResult = await dispatch(actionCreator);
      if (isSkippedThunk(actionResult)) {
        return undefined;
      }
      const result = unwrapResult(actionResult);

      return result;
    } catch (error) {
      // Handle 401 errors silently - modal is already shown by callApi
      // unwrapResult wraps rejected actions, so check error.payload for the actual error value
      const errorValue = error?.payload || error;
      const is401 = 
        error === 401 || 
        errorValue === 401 ||
        error?.status === 401 || 
        errorValue?.status === 401 ||
        error?.error === 401 ||
        errorValue?.error === 401 ||
        (typeof errorValue === 'string' && errorValue.includes('401')) ||
        (typeof error === 'string' && error.includes('401'));
      
      if (is401) {
        // Don't throw 401 errors - modal is already shown
        console.warn('401 Unauthorized - session expired modal shown');
        return { error: 'Unauthorized', status: 401 };
      }

      // Re-throw so try/catch screens can show a banner, including the
      // fallback message "Error" when the server body is empty.
      if (process.env.NODE_ENV === 'development') {
        console.warn(
          'CustomDispatch throwing error for backward compatibility:',
          getApiErrorMessage(errorValue, "Error")
        );
      }
      throw error;
    }
  };
};

// Use this ONLY for components that don't use try-catch with dispatch
export const createCustomDispatchReturnErrors = (dispatch) => {
  return async (actionCreator) => {
    try {
      const actionResult = await dispatch(actionCreator);
      if (isSkippedThunk(actionResult)) {
        return undefined;
      }
      const result = unwrapResult(actionResult);

      return result;
    } catch (error) {
      console.error('CustomDispatch returning error object:', error);
      
      return { error: getApiErrorMessage(error?.payload ?? error, "Error") };
    }
  };
};
